/**
 * Returns a safe, same-origin *internal* path suitable for router navigation or
 * `window.location`. Guards the post-login redirect flow against open redirects:
 * rejects absolute URLs, protocol-relative (`//host`) and backslash-bypass
 * (`/\host`, `%5C`) payloads that a crafted pre-login URL could smuggle in via
 * `window.location.pathname`. Anything unsafe falls back to `/`.
 */
export function sanitizeInternalPath(
  candidate: string | null | undefined,
  fallback = '/',
  origin: string = window.location.origin
): string {
  if (typeof candidate !== 'string' || candidate.length === 0) return fallback
  // Must be a path reference — not absolute, protocol-relative, or backslash-prefixed
  if (!candidate.startsWith('/') || candidate.startsWith('//') || candidate.startsWith('/\\')) return fallback
  // Internal paths never contain backslashes (literal or percent-encoded)
  if (candidate.includes('\\') || /%5c/i.test(candidate)) return fallback
  try {
    const resolved = new URL(candidate, origin)
    // A same-origin relative path resolves back to our own origin; anything else is external
    if (resolved.origin !== origin) return fallback
    return `${resolved.pathname}${resolved.search}${resolved.hash}`
  } catch {
    return fallback
  }
}
